import groupBy from 'lodash/groupBy';
import {
  DayOfWeekIndex,
  OpeningHours,
  OpeningHoursByDayOfWeek,
} from 'lib/components/OpeningHoursList/common';
import { OpeningHoursAction } from './actions';

export interface OpeningHoursState {
  saving: boolean;
  restaurant: {
    byDayOfWeek: OpeningHoursByDayOfWeek;
    pristine?: OpeningHoursByDayOfWeek;
    areModified?: boolean;
    loading: boolean;
    error?: null | string;
  };
  zone: {
    byDayOfWeek: OpeningHoursByDayOfWeek;
    loading: boolean;
    error?: null | string;
  };
}

export const initialState: OpeningHoursState = {
  saving: false,
  restaurant: {
    byDayOfWeek: {},
    areModified: false,
    loading: true,
  },
  zone: {
    byDayOfWeek: {},
    loading: false,
  },
};

export interface DefinedOpeningHours {
  local_start_time: string;
  local_end_time: string;
  day_of_week: DayOfWeekIndex;
}

function removeTrailingSeconds(hours: DefinedOpeningHours[]) {
  return hours.map((it) => ({
    ...it,
    local_start_time: it.local_start_time.substring(
      0,
      it.local_start_time.length - 3,
    ),
    local_end_time: it.local_end_time.substring(
      0,
      it.local_end_time.length - 3,
    ),
  }));
}

function groupByDayOfWeek(hours: DefinedOpeningHours[]) {
  return groupBy(hours, (it) => it.day_of_week);
}

function addNew(
  byDayOfWeek: OpeningHoursByDayOfWeek,
  dayOfWeekIndex: DayOfWeekIndex,
) {
  const openingHoursWithNew = (byDayOfWeek[dayOfWeekIndex] || []).concat({
    day_of_week: dayOfWeekIndex,
    local_start_time: null,
    local_end_time: null,
  });
  return Object.assign({}, byDayOfWeek, {
    [dayOfWeekIndex]: openingHoursWithNew,
  });
}

function remove(
  byDayOfWeek: OpeningHoursByDayOfWeek,
  dayOfWeekIndex: DayOfWeekIndex,
  hoursIndex: number,
) {
  const hoursForDayOfWeek = [...byDayOfWeek[dayOfWeekIndex]!];
  hoursForDayOfWeek.splice(hoursIndex, 1);
  return Object.assign({}, byDayOfWeek, {
    [dayOfWeekIndex]: hoursForDayOfWeek,
  });
}

function modify(
  byDayOfWeek: OpeningHoursByDayOfWeek,
  newHours: OpeningHours,
  hoursIndex: number,
  dayOfWeekIndex: DayOfWeekIndex,
) {
  const hoursForDayOfWeek = byDayOfWeek[dayOfWeekIndex]
    ? [...byDayOfWeek[dayOfWeekIndex]!]
    : [];
  hoursForDayOfWeek[hoursIndex] = newHours;
  return Object.assign({}, byDayOfWeek, {
    [dayOfWeekIndex]: hoursForDayOfWeek,
  });
}

const openingHoursReducer = (
  state: OpeningHoursState = initialState,
  action: OpeningHoursAction,
): OpeningHoursState => {
  switch (action.type) {
    case 'GET_RESTAURANT_OPENING_HOURS_REQUEST': {
      return {
        ...state,
        restaurant: {
          ...state.restaurant,
          error: null,
          loading: true,
        },
      };
    }

    case 'GET_RESTAURANT_OPENING_HOURS_SUCCESS': {
      return {
        ...state,
        restaurant: {
          byDayOfWeek: groupByDayOfWeek(
            removeTrailingSeconds(action.payload.hours),
          ),
          pristine: groupByDayOfWeek(
            removeTrailingSeconds(action.payload.hours),
          ),
          loading: false,
        },
      };
    }

    case 'GET_RESTAURANT_OPENING_HOURS_FAILURE': {
      return {
        ...state,
        restaurant: {
          ...state.restaurant,
          byDayOfWeek: {},
          loading: false,
          error: 'load_error',
        },
      };
    }

    case 'GET_ZONE_OPENING_HOURS_REQUEST': {
      return {
        ...state,
        zone: {
          ...state.zone,
          error: null,
          loading: true,
        },
      };
    }

    case 'GET_ZONE_OPENING_HOURS_SUCCESS': {
      return {
        ...state,
        zone: {
          byDayOfWeek: groupByDayOfWeek(
            removeTrailingSeconds(action.payload.hours),
          ),
          loading: false,
        },
      };
    }

    case 'GET_ZONE_OPENING_HOURS_FAILURE': {
      return {
        ...state,
        zone: {
          ...state.zone,
          byDayOfWeek: {},
          loading: false,
          error: 'load_error',
        },
      };
    }

    case 'UPDATE_RESTAURANT_OPENING_HOURS': {
      return {
        ...state,
        restaurant: {
          ...state.restaurant,
          areModified: true,
          byDayOfWeek: modify(
            state.restaurant.byDayOfWeek,
            action.newHours,
            action.hoursIndex,
            action.dayOfWeekIndex,
          ),
        },
      };
    }

    case 'ADD_NEW_RESTAURANT_OPENING_HOURS': {
      return {
        ...state,
        restaurant: {
          ...state.restaurant,
          areModified: true,
          byDayOfWeek: addNew(
            state.restaurant.byDayOfWeek,
            action.dayOfWeekIndex,
          ),
        },
      };
    }

    case 'REMOVE_RESTAURANT_OPENING_HOURS': {
      return {
        ...state,
        restaurant: {
          ...state.restaurant,
          areModified: true,
          byDayOfWeek: remove(
            state.restaurant.byDayOfWeek,
            action.dayOfWeekIndex,
            action.hoursIndex,
          ),
        },
      };
    }

    case 'REMOVE_OPENING_HOURS_FOR_DAY': {
      return {
        ...state,
        restaurant: {
          ...state.restaurant,
          areModified: true,
          byDayOfWeek: Object.assign({}, state.restaurant.byDayOfWeek, {
            [action.dayIndex]: [],
          }),
        },
      };
    }

    case 'SAVE_UPDATED_RESTAURANT_OPENING_HOURS_REQUEST':
    case 'SAVE_UPDATED_ONBOARDING_OPENING_HOURS_REQUEST': {
      return {
        ...state,
        saving: true,
        restaurant: {
          ...state.restaurant,
          error: null,
        },
      };
    }
    case 'SAVE_UPDATED_RESTAURANT_OPENING_HOURS_SUCCESS':
    case 'SAVE_UPDATED_ONBOARDING_OPENING_HOURS_SUCCESS': {
      return {
        ...state,
        saving: false,
        restaurant: {
          ...state.restaurant,
          error: null,
        },
      };
    }
    case 'SAVE_UPDATED_RESTAURANT_OPENING_HOURS_FAILURE':
    case 'SAVE_UPDATED_ONBOARDING_OPENING_HOURS_FAILURE': {
      return {
        ...state,
        saving: false,
        restaurant: {
          ...state.restaurant,
          error: 'saving_error',
        },
      };
    }

    default:
      return { ...state };
  }
};

export default openingHoursReducer;
